import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Mail, Smartphone, Loader2, KeyRound } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const err = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'No se pudo verificar');

export const MfaChallenge = ({ challenge, onDone, onCancel, onVerify, resendPath = '/auth/2fa/send-email' }) => {
  const { verifyMfa, api } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const isEmail = challenge.method === 'email';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await (onVerify || verifyMfa)(challenge.mfa_token, code.trim());
      await onDone();
    } catch (e2) {
      toast.error(err(e2));
      if (e2.response?.status === 429) onCancel();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post(resendPath, { mfa_token: challenge.mfa_token });
      toast.success(`Código enviado a ${data.email_hint}`);
    } catch (e2) {
      toast.error(err(e2));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 animate-fade-in" data-testid="mfa-challenge-form">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
        <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
        <div className="text-sm text-slate-300">
          <p className="font-semibold text-white">Verificación en dos pasos</p>
          {recovery ? <p>Ingresa uno de tus códigos de recuperación (formato xxxxxx-xxxxxx).</p>
            : isEmail ? <p>Enviamos un código de 6 dígitos a <b>{challenge.email_hint}</b>.</p>
            : <p>Abre tu app autenticadora y escribe el código de 6 dígitos de NotaryOS.</p>}
        </div>
      </div>
      <div className="relative">
        {recovery ? <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          : isEmail ? <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          : <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />}
        <input autoFocus value={code} onChange={(e) => setCode(e.target.value)} inputMode={recovery ? 'text' : 'numeric'} maxLength={recovery ? 13 : 6}
          placeholder={recovery ? 'abc123-def456' : '000000'} data-testid="mfa-code-input"
          className="w-full pl-11 pr-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg focus:border-emerald-500 text-white tracking-[0.4em] font-mono text-lg" />
      </div>
      <button type="submit" disabled={busy || code.trim().length < 6} data-testid="mfa-verify-btn"
        className="w-full btn-primary py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Verificar e ingresar
      </button>
      <div className="flex flex-wrap justify-between gap-2 text-xs">
        {isEmail && !recovery && <button type="button" onClick={resend} data-testid="mfa-resend-btn" className="text-blue-400 hover:underline">Reenviar código</button>}
        <button type="button" onClick={() => { setRecovery(!recovery); setCode(''); }} data-testid="mfa-toggle-recovery-btn" className="text-blue-400 hover:underline">
          {recovery ? 'Usar código normal' : 'Usar código de recuperación'}
        </button>
        <button type="button" onClick={onCancel} data-testid="mfa-cancel-btn" className="text-slate-400 hover:text-white">Volver</button>
      </div>
    </form>
  );
};
